import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AiSummary,
  AiSummaryEntityType,
  exportAiSummary,
  generateAiSummary,
  getAiSummaries
} from '../../../Api/AI/aiSummariesApi';
import { downloadBlob } from '../../../utils/fileDownload';
import { formatGeneratedAt } from '../../../utils/summaryDate';
import { Card } from '../../Atoms/Card';

// The generation job runs asynchronously, so after a 202 we poll GET until a newer snapshot lands.
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

// The context label is date-derived; the info popover surfaces the exact generation timestamp.
const GENERATED_AT_PATTERN = "MMM d, yyyy 'at' h:mm a";

export interface StatusUpdateSectionProps {
  entityId?: string;
  entityType?: AiSummaryEntityType;
  // Shown when no entity is in scope, in place of the generic empty state; keeps this component domain-agnostic.
  emptyEntityPrompt?: string;
}

export const StatusUpdateSection = ({
  entityId,
  entityType = 'Project',
  emptyEntityPrompt
}: StatusUpdateSectionProps) => {
  const [summaries, setSummaries] = useState<AiSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  // Index into the newest-first summaries list; 0 is the most recent snapshot.
  const [selectedIndex, setSelectedIndex] = useState(0);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Bumped when the entity changes and on unmount. Async work compares the value it started with, so a
  // response for the previous account or project never lands on the one now shown.
  const run = useRef(0);

  const current = summaries[selectedIndex];
  const generatedAt = current ? formatGeneratedAt(current.generatedAt, GENERATED_AT_PATTERN) : '';
  // Newest is index 0; older snapshots have higher indices.
  const canViewNewer = selectedIndex > 0;
  const canViewOlder = selectedIndex < summaries.length - 1;

  const load = useCallback(async () => {
    if (!entityId) {
      // No entity selected — clear any prior state and fall through to the empty state
      // rather than leaving the initial spinner running forever.
      setSummaries([]);
      setLoadError(false);
      setLoading(false);
      return;
    }
    const startedRun = run.current;
    setLoading(true);
    setLoadError(false);
    const { data, error } = await getAiSummaries(entityType, entityId);
    if (startedRun !== run.current) return;
    if (error || !data) {
      setLoadError(true);
    } else {
      setSummaries(data);
      setSelectedIndex(0);
    }
    setLoading(false);
  }, [entityId, entityType]);

  useEffect(() => {
    // A refresh or export in flight belongs to the previous entity, and its result is now discarded.
    setRefreshing(false);
    setRefreshError(false);
    setExporting(false);
    setExportError(false);
    load();
    return () => {
      run.current += 1;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, [load]);

  const pollForNewSummary = useCallback(
    (previousLatestId: string | undefined, attempt: number) => {
      const startedRun = run.current;
      pollTimer.current = setTimeout(async () => {
        const { data } = await getAiSummaries(entityType, entityId);
        if (startedRun !== run.current) return;
        const newLatest = data?.[0];
        if (data && newLatest && newLatest.id !== previousLatestId) {
          setSummaries(data);
          setSelectedIndex(0);
          setRefreshing(false);
          return;
        }
        if (attempt + 1 >= MAX_POLL_ATTEMPTS) {
          setRefreshing(false);
          setRefreshError(true);
          return;
        }
        pollForNewSummary(previousLatestId, attempt + 1);
      }, POLL_INTERVAL_MS);
    },
    [entityId, entityType]
  );

  const handleRefresh = useCallback(async () => {
    if (!entityId || refreshing) return;
    const startedRun = run.current;
    setRefreshing(true);
    setRefreshError(false);
    const previousLatestId = summaries[0]?.id;
    const { error } = await generateAiSummary(entityType, entityId);
    if (startedRun !== run.current) return;
    if (error) {
      setRefreshing(false);
      setRefreshError(true);
      return;
    }
    pollForNewSummary(previousLatestId, 0);
  }, [entityId, entityType, refreshing, summaries, pollForNewSummary]);

  // A summary still generating would be replaced as soon as it lands, so export waits for it.
  const canExport = !!current && !loading && !refreshing && !exporting;

  const handleExport = useCallback(async () => {
    if (!current || !canExport) return;
    const startedRun = run.current;
    setExporting(true);
    setExportError(false);
    const { data, error } = await exportAiSummary(current.id);
    if (startedRun !== run.current) return;
    setExporting(false);
    if (error || !data) {
      setExportError(true);
      return;
    }
    try {
      downloadBlob(data.blob, data.filename ?? `Executive Summary - ${current.contextLabel}.pdf`);
    } catch {
      setExportError(true);
    }
  }, [current, canExport]);

  return (
    <Card
      useDefaultCardStyles={false}
      className="text-white"
      data-testid="status-update-section"
      data-cy="status-update-section">
      <div className="flex items-start justify-between gap-4 rounded-lg bg-white/10 px-5 py-3">
        <div className="min-w-0">
          <div className="text-xs text-grey-4">Status Update</div>
          {current && (
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-white truncate">{current.contextLabel}</span>
              <Tooltip
                title={generatedAt ? `Generated ${generatedAt}` : ''}
                arrow
                placement="top">
                <button
                  type="button"
                  aria-label="Summary generated date"
                  data-testid="status-update-generated-info"
                  data-cy="status-update-generated-info"
                  className="flex shrink-0 items-center text-grey-4 hover:text-white">
                  <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Tooltip
            title={refreshing ? 'Export is available once the new summary is ready' : 'Export PDF'}
            arrow
            placement="top">
            <span>
              <button
                type="button"
                onClick={handleExport}
                disabled={!canExport}
                aria-label="Export summary as PDF"
                data-testid="status-update-export"
                data-cy="status-update-export"
                className="flex h-7 w-7 items-center justify-center rounded-full text-grey-3 hover:bg-white/10 hover:text-white disabled:opacity-60 disabled:hover:bg-transparent">
                {exporting ? (
                  <CircularProgress
                    size={16}
                    sx={{ color: 'inherit' }}
                  />
                ) : (
                  <FileDownloadOutlinedIcon fontSize="small" />
                )}
              </button>
            </span>
          </Tooltip>
          {summaries.length > 1 && (
            <div
              className="flex items-center gap-1"
              data-testid="status-update-nav">
              <button
                type="button"
                onClick={() => setSelectedIndex((i) => i + 1)}
                disabled={!canViewOlder}
                aria-label="Older summary"
                data-testid="status-update-older"
                data-cy="status-update-older"
                className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-white/10">
                <ChevronLeftIcon fontSize="small" />
              </button>
              <button
                type="button"
                onClick={() => setSelectedIndex((i) => i - 1)}
                disabled={!canViewNewer}
                aria-label="Newer summary"
                data-testid="status-update-newer"
                data-cy="status-update-newer"
                className="flex h-7 w-7 items-center justify-center rounded-md bg-white/10 text-white hover:bg-white/20 disabled:opacity-40 disabled:hover:bg-white/10">
                <ChevronRightIcon fontSize="small" />
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label="Refresh summary"
            data-testid="status-update-refresh"
            data-cy="status-update-refresh"
            className="flex h-7 w-7 items-center justify-center rounded-full text-grey-3 hover:bg-white/10 hover:text-white disabled:opacity-60">
            {refreshing ? (
              <CircularProgress
                size={16}
                sx={{ color: 'inherit' }}
              />
            ) : (
              <RefreshIcon fontSize="small" />
            )}
          </button>
        </div>
      </div>

      {loading ? (
        <div
          className="flex justify-center py-6"
          data-testid="status-update-loading">
          <CircularProgress
            size={20}
            sx={{ color: 'inherit' }}
          />
        </div>
      ) : loadError ? (
        <div
          className="mt-3 text-sm text-grey-3"
          data-testid="status-update-error">
          <span>Unable to load the latest summary.</span>
          <button
            type="button"
            onClick={load}
            data-testid="status-update-retry"
            className="ml-2 font-semibold text-white underline">
            Retry
          </button>
        </div>
      ) : !entityId && emptyEntityPrompt ? (
        <div
          className="mt-3 text-sm text-grey-4"
          data-testid="status-update-no-entity">
          {emptyEntityPrompt}
        </div>
      ) : !current ? (
        <div
          className="mt-3 text-sm text-grey-4"
          data-testid="status-update-empty">
          No summary has been generated yet.
        </div>
      ) : (
        <div className="mt-3">
          <div className="text-sm font-semibold text-white mb-1">Executive Summary</div>
          <p
            className="text-sm text-grey-3 whitespace-pre-line"
            data-testid="status-update-summary-text">
            {current.summaryText}
          </p>
        </div>
      )}

      {refreshError && (
        <div
          className="mt-3 text-sm text-grey-3"
          data-testid="status-update-refresh-error">
          <span>Summary generation failed.</span>
          <button
            type="button"
            onClick={handleRefresh}
            data-testid="status-update-refresh-retry"
            className="ml-2 font-semibold text-white underline">
            Retry
          </button>
        </div>
      )}

      {exportError && (
        <div
          className="mt-3 text-sm text-grey-3"
          data-testid="status-update-export-error">
          <span>PDF export failed.</span>
          <button
            type="button"
            onClick={handleExport}
            disabled={!canExport}
            data-testid="status-update-export-retry"
            className="ml-2 font-semibold text-white underline">
            Retry
          </button>
        </div>
      )}
    </Card>
  );
};
